/// Day-scoped Daily Challenge leaderboard overlay. One board per day (all
/// difficulties together; each row shows its difficulty). Fetches today's
/// top 50 on mount. Mirrors LeaderboardPanel's look, minus the difficulty tabs.
import { useEffect, useMemo, useState } from 'react'
import { fetchDailyLeaderboard, type LeaderboardEntry } from '../data/leaderboard'

export function DailyLeaderboardPanel(props: {
  date: string
  levelDisplayName: string
  currentUid: string | null
  onClose: () => void
}) {
  const [entries, setEntries] = useState<LeaderboardEntry[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setEntries(null)
    setError(null)
    fetchDailyLeaderboard(props.date)
      .then((data) => {
        if (!cancelled) setEntries(data)
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e))
          setEntries([])
        }
      })
    return () => {
      cancelled = true
    }
  }, [props.date])

  const myRow = useMemo(() => {
    if (!entries || !props.currentUid) return -1
    return entries.findIndex((e) => e.uid === props.currentUid)
  }, [entries, props.currentUid])

  return (
    <div
      className="pointer-events-auto fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      onClick={props.onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[85vh] w-full max-w-md flex-col rounded-xl bg-slate-900 p-5 text-slate-100 shadow-2xl ring-1 ring-white/10"
      >
        <div className="flex items-baseline justify-between">
          <h3 className="text-lg font-semibold tracking-wide">
            🏆 Daily Challenge
            <span className="ml-2 text-xs font-normal text-slate-400">· {props.date}</span>
          </h3>
          <button onClick={props.onClose} className="text-xs text-slate-400 hover:text-white" aria-label="Close">
            ✕
          </button>
        </div>
        <div className="mt-1 text-xs text-slate-400">{props.levelDisplayName} · resets 00:00 UTC</div>

        {error ? (
          <div className="mt-4 rounded-md bg-rose-900/40 px-3 py-3 text-sm text-rose-200 ring-1 ring-rose-500/30">
            Couldn't load board: {error}
          </div>
        ) : entries === null ? (
          <div className="mt-4 flex h-32 items-center justify-center text-sm text-slate-400">Loading…</div>
        ) : entries.length === 0 ? (
          <div className="mt-4 flex h-32 items-center justify-center text-sm text-slate-400">
            No scores yet — be the first.
          </div>
        ) : (
          <div className="mt-3 overflow-y-auto rounded-md bg-slate-800/40 ring-1 ring-white/5">
            <table className="w-full text-sm">
              <tbody>
                {entries.map((entry, idx) => {
                  const isMe = idx === myRow
                  return (
                    <tr
                      key={entry.uid}
                      className={'border-b border-white/5 last:border-0 ' + (isMe ? 'bg-emerald-900/30' : '')}
                    >
                      <td className="w-10 px-3 py-1.5 text-right font-mono text-xs text-slate-400">{idx + 1}</td>
                      <td className="truncate px-2 py-1.5">
                        <span className={isMe ? 'font-semibold text-emerald-200' : ''}>{entry.displayName}</span>
                        {isMe && (
                          <span className="ml-1.5 text-[10px] uppercase tracking-wider text-emerald-300">you</span>
                        )}
                        <span className="ml-1.5 text-[10px] text-slate-500">{entry.difficulty}</span>
                      </td>
                      <td className="w-20 px-3 py-1.5 text-right font-mono">{entry.score.toLocaleString()}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
